// GET /api/accounts, reconciled per the 2026-09-06 UX execution plan §4.
//
// Decision (frozen; see the plan for the full rationale): the backend
// envelope shape wins -- `{data: [...], meta}` with `data` itself the array,
// matching every other collection endpoint in this codebase -- and the
// frontend's field set wins on substance. This hook returns the WIRE SHAPE
// UNRENAMED: no `account_id -> id` / `display_name -> name` mapping layer. A
// second vocabulary is how the two sides drifted in the first place, so
// components read `account.display_name`, `account.last4`, etc. directly.
//
// `status` is CONNECTION health (never conflated with balance honesty).
// `balance_status` is a SEPARATE field for balance honesty. M1 only emits
// `balance_status` in {ok, insufficient}; the union below keeps all four
// values so the partial/stale/derived UI is typed and testable now even
// though only fixtures exercise it until snapshots ship (§4 rule 6).
//
// `balance` is a SIGNED decimal string at the source (assets positive,
// credit cards and loans negative) so a column of rows visibly sums to the
// header -- the frontend never negates by `kind`. `balance: null` is the
// ONLY representation of "never reported"; it is never coerced to '0'.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import type { ReadScope } from './useReadScope';

export const accountKindSchema = z.enum([
  'checking',
  'savings',
  'cash_like',
  'credit',
  'loan',
  'investment',
]);

export const connectionStatusSchema = z.enum(['synced', 'needs_login', 'error', 'manual']);

export const balanceStatusSchema = z.enum(['ok', 'derived', 'stale', 'insufficient']);
export const reconnectMatchStatusSchema = z.enum(['auto_matched', 'auto_match_uncertain']);
export const reconnectResumeStatusSchema = z.enum(['running', 'failed', 'complete']);

export const accountSchema = z.object({
  account_id: z.string(),
  item_id: z.string().nullable(),
  source: z.enum(['plaid', 'manual']),
  institution_id: z.string().nullable(),
  institution_name: z.string().nullable(),
  kind: accountKindSchema,
  is_liability: z.boolean(),
  business_use: z.enum(['none', 'mixed', 'primary']),
  display_name: z.string(),
  last4: z.string().nullable(),
  currency: z.string(),
  status: connectionStatusSchema,
  status_detail: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  // SIGNED decimal string; null when never reported -- never coerced to '0'.
  balance: z.string().nullable(),
  balance_status: balanceStatusSchema,
  as_of: z.string().nullable(),
  reconnect_match_status: reconnectMatchStatusSchema.nullable(),
  reconnect_breakpoint_date: z.iso.date().nullable(),
  reconnect_resume_status: reconnectResumeStatusSchema.nullable(),
  reconnect_imported_count: z.number().int().nonnegative().nullable(),
  reconnect_skipped_retained_count: z.number().int().nonnegative().nullable(),
}).strict();

export type AccountKind = z.infer<typeof accountKindSchema>;
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;
export type BalanceStatus = z.infer<typeof balanceStatusSchema>;
export type Account = z.infer<typeof accountSchema>;

export const canonicalAccountSchema = accountSchema.extend({
  canonical_account_id: z.string().uuid().nullable(),
  representative_account_id: z.string().uuid(),
  physical_account_ids: z.array(z.string().uuid()),
  active_item_ids: z.array(z.string().uuid()),
  source_state: z.enum(['active', 'retained', 'manual']),
  reconciliation_initialization_required: z.boolean(),
  reconciliation_review_status: z.enum(['needs_review', 'finished']).nullable(),
  reconciliation_review_generation: z.number().int().nonnegative().nullable(),
});

export type CanonicalAccount = z.infer<typeof canonicalAccountSchema>;

// `data` IS the array -- there is no `data.accounts` wrapper.
const accountsResponseSchema = z.array(accountSchema);
const primaryAccountsResponseSchema = z.array(canonicalAccountSchema);
const primaryMetaSchema = z.union([
  z.object({}).strict(),
  z.object({ reconciliation_initialization_required: z.literal(true) }).strict(),
]);
const diagnosticMetaSchema = z.object({ diagnostic_scope: z.literal(true) }).strict();

export function parseAccountsResponse(payload: unknown, scope?: ReadScope) {
  if (scope !== undefined) {
    const parsed = parseEnvelope(accountsResponseSchema, payload);
    const meta = diagnosticMetaSchema.parse(parsed.meta);
    return { data: parsed.data, meta };
  }
  const parsed = parseEnvelope(primaryAccountsResponseSchema, payload);
  const meta = primaryMetaSchema.parse(parsed.meta);
  return { data: parsed.data, meta };
}

/** `checking|savings|cash_like` -> "Depository", per the design's Type column (§4 rule 5). */
export function accountClassLabel(kind: AccountKind): string {
  switch (kind) {
    case 'checking':
    case 'savings':
    case 'cash_like':
      return 'Depository';
    case 'credit':
      return 'Credit';
    case 'loan':
      return 'Loan';
    case 'investment':
      return 'Investment';
    default:
      return kind;
  }
}

export function useAccounts(scope?: ReadScope) {
  const queryScope = scope ?? 'primary';
  return useQuery({
    queryKey: ['accounts', { scope: queryScope }],
    queryFn: async () => {
      const params = scope === undefined ? {} : { scope };
      const response = await client.get('/api/accounts', { params });
      return parseAccountsResponse(response.data, scope).data;
    },
  });
}
